// Wedding App Authentication API Service

import { parseApiError } from 'utils/api-client';
import { getAccessToken } from 'utils/sessionStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export interface LoginCredentials {
  email: string;
  password: string;
  turnstileToken?: string;
}

export interface LoginResponse {
  success?: boolean;
  token: string;
  refreshToken?: string;
  joinCode?: string;
  couple: {
    id: string;
    email: string;
    eventId: string;
    displayName: string;
    isEmailVerified?: boolean;
  };
}

/**
 * Login for couple users
 */
export async function loginCouple(credentials: LoginCredentials): Promise<LoginResponse> {
  const response = await fetch(`${API_URL}/api/couple/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    throw await parseApiError(response, 'Login failed');
  }

  return response.json();
}

/**
 * Verify email with 6-digit code
 */
export async function verifyEmail(
  code: string,
): Promise<{ success: boolean; alreadyVerified?: boolean }> {
  const token = getAccessToken();
  const response = await fetch(`${API_URL}/api/couple/verify-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    throw await parseApiError(response, 'Verification failed');
  }

  return response.json();
}

/**
 * Resend verification code
 */
export async function resendVerification(): Promise<{
  success: boolean;
  alreadyVerified?: boolean;
}> {
  const token = getAccessToken();
  const response = await fetch(`${API_URL}/api/couple/resend-verification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to resend code');
  }

  return response.json();
}

/**
 * Join event for guest users
 */
export interface JoinEventCredentials {
  code: string;
  displayName: string;
}

export interface JoinEventResponse {
  token: string;
  refreshToken?: string;
  eventId: string;
  uploaderId: string;
  displayName: string;
  plan?: 'free' | 'basic' | 'pro';
  eventType?: 'wedding' | 'baptism' | 'wedding_baptism';
}

export async function joinEvent(
  eventId: string,
  credentials: JoinEventCredentials,
): Promise<JoinEventResponse> {
  const response = await fetch(`${API_URL}/api/events/${eventId}/join`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    throw await parseApiError(response, 'Join failed');
  }

  return response.json();
}
